import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { useToast } from '@/components/ui/toastContext'
import {
  NOTIFICATIONS_LIST_KEY,
  NOTIFICATIONS_UNREAD_COUNT_KEY,
} from '@/features/notifications/hooks/useNotifications'
import { disconnectEcho, getEcho } from '@/lib/echo'
import { useAuthStore } from '@/store/authStore'

interface BroadcastNotificationPayload {
  title?: string
  body?: string
}

/**
 * Subscribes to the user's private notification channel for the lifetime of
 * an authenticated session. `.notification()` is Laravel Echo's built-in
 * handler for the default `Illuminate\Notifications\Events\
 * BroadcastNotificationCreated` event — every notification in this app
 * uses that default (none define a custom toBroadcast()), so this is the
 * whole integration, no custom event name needed.
 */
export function useNotificationSocket() {
  const userId = useAuthStore((s) => s.user?.id)
  const queryClient = useQueryClient()
  const toast = useToast()

  useEffect(() => {
    if (!userId) return

    const echo = getEcho()
    const channel = echo.private(`App.Models.User.${userId}`)

    channel.notification((notification: BroadcastNotificationPayload) => {
      void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_LIST_KEY })
      void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_UNREAD_COUNT_KEY })
      toast.info(notification.title ?? notification.body ?? 'You have a new notification.')
    })

    return () => {
      echo.leave(`App.Models.User.${userId}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId])

  useEffect(() => {
    if (!userId) {
      disconnectEcho()
    }
  }, [userId])
}
